import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { CheckboxCellRenderer, ColumnApi, GridApi, ValueFormatterParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import * as Bixby from '$/core';
import { getClients, setCurrentClient, createClient } from '$/store/client/actions';
import { AppState } from '$/store';
import HeadingBlock from '$/components/global/heading';
import Modal from '$/components/global/modal';
import AddClient from './add-client';
import { AddClientFormValues } from './forms/AddClientForm';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import './index.scss';

const getAllClients = getClients;

interface IClientProps {
  isLoading: boolean;
  clients: Bixby.Models.ClientSlim[];
  dispatchGetClients: () => Promise<void>;
  dispatchAddClient: (request: Bixby.Models.CreateClientRequest) => Promise<void>;
  dispatchSetCurrentClient: (clientSlim?: Bixby.Models.ClientSlim) => Promise<void>;
}

const ClientList: React.FC<IClientProps> = props => {
  const { isLoading, clients, dispatchGetClients, dispatchAddClient, dispatchSetCurrentClient } = props;

  const [modalActive, setModalActive] = useState(false);
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);

  useEffect(() => {
    const getClientDispatcher = async () => {
      dispatchSetCurrentClient(undefined);
      await dispatchGetClients();
    };
    getClientDispatcher();
  }, [dispatchGetClients, dispatchSetCurrentClient]);

  const dateValueFormatter = (params: ValueFormatterParams) => {
    const accessDate: dayjs.Dayjs | null = params.value ? dayjs.utc(params.value) : null;
    const displayDate: string = accessDate && accessDate.isValid() ? Bixby.Utils.formatDate(accessDate) : '';
    return displayDate;
  };

  const linkCellFormatter = (params: any) => {
    const url = '/admin/client/' + params.data['id'];
    return (
      <a className="link_underline bix-link" href={url}>
        {' '}
        {params.value}{' '}
      </a>
    );
  };

  const handleOpenAddModal = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    setModalActive(true);
  };

  const handleAdd = async (values: AddClientFormValues) => {
    setModalActive(false);
    const request: Bixby.Models.CreateClientRequest = {
      bixbyClientEmail: values.bixbyClientEmail,
      dataSiteEmail: values.dataSiteEmail,
      emailDomain: values.emailDomain,
      name: values.clientName,
      notes: values.clientNotes,
      subscription_Id: values.subscriptionTier,
      usersCannotLogin: values.usersCannotLogin,
    };
    await dispatchAddClient(request);
    return Promise.resolve();
  };

  const handleModalDismiss = () => {
    setModalActive(false);
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = (params: any) => {
    if (params.column.colId === 'name') {
      return [];
    }

    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <React.Fragment>
        <HeadingBlock
          title={'Manage Clients'}
          addButton={{ enabled: true, text: 'Add Client', onClick: handleOpenAddModal }}
        />
        {clients && !Bixby.Utils.isNullOrEmpty(clients) ? (
          <section className="client_list">
            <div className="clients_data_grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    enableRangeSelection={true}
                    getContextMenuItems={getContextMenuItems}
                    defaultColDef={{
                      flex: 1,
                      minWidth: 100,
                      sortable: true,
                      resizable: true,
                      lockPinned: true,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                    }}
                    onGridReady={onGridReady}
                    rowData={clients}
                    suppressDragLeaveHidesColumns={true}
                    suppressMovableColumns={true}
                    animateRows={true}
                    rowHeight={32}
                    headerHeight={32}
                    columnDefs={[
                      {
                        field: 'name',
                        headerName: 'Name',
                        minWidth: 200,
                        cellRenderer: linkCellFormatter,
                        pinned: 'left',
                        floatingFilter: true,
                        filter: 'agTextColumnFilter',
                      },
                      { field: 'emailDomain', headerName: 'Email Domain', minWidth: 200 },
                      { field: 'bixbyClientEmail', headerName: 'Email', minWidth: 200 },
                      { field: 'subscription_Name', headerName: 'Subscription Tier', minWidth: 200 },
                      {
                        field: 'firstAccess',
                        headerName: 'Date of 1st Access',
                        filter: false,
                        minWidth: 175,
                        valueFormatter: dateValueFormatter,
                      },
                      {
                        field: 'daysOnPortal',
                        headerName: 'Days on Portal',
                        filter: 'agNumberColumnFilter',
                        minWidth: 175,
                      },
                      {
                        field: 'companiesAccessedTotal',
                        headerName: 'Unique Names',
                        filter: 'agNumberColumnFilter',
                        minWidth: 175,
                      },
                      {
                        field: 'companiesAccessedCurrent',
                        headerName: 'Current Names',
                        filter: 'agNumberColumnFilter',
                        minWidth: 175,
                      },
                      {
                        field: 'lastAccess',
                        headerName: 'Last Client Login',
                        filter: false,
                        minWidth: 200,
                        valueFormatter: dateValueFormatter,
                      },
                      {
                        field: 'clientId',
                        headerName: 'Has Api',
                        filter: false,
                        minWidth: 100,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                        cellRenderer: e => <div title="${e.data['clientId']}"> {e.value ? 'Yes' : ''}</div>,
                      },
                      {
                        field: 'hasAnalytics',
                        headerName: 'Has Analytics',
                        filter: false,
                        minWidth: 150,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'hasExcelAccess',
                        headerName: 'Has Excel Access',
                        filter: false,
                        minWidth: 160,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'includeWithDataAggregation',
                        headerName: 'Include with Data Aggregation',
                        filter: false,
                        minWidth: 250,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'hasCompanyTranscriptions',
                        headerName: 'Has Transcripts',
                        filter: false,
                        minWidth: 150,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'hasCompanyDealNotes',
                        headerName: 'Has Deal Notes',
                        filter: false,
                        minWidth: 150,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'hasPublicCompanies',
                        headerName: 'Has Public Companies',
                        filter: false,
                        minWidth: 200,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'hasCompanyDocuments',
                        headerName: 'Has Company Documents',
                        filter: false,
                        minWidth: 200,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'hasCloAccess',
                        headerName: 'Has CLO Access',
                        filter: false,
                        minWidth: 200,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'hasCreditSightsCovenantReview',
                        headerName: 'Has CreditSights Covenant Review Access',
                        filter: false,
                        minWidth: 200,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'hasPreapprovedCompanyAccess',
                        headerName: 'Has Preappoved Company Access',
                        filter: false,
                        minWidth: 200,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'isOnFitchPaper',
                        headerName: 'Is on Fitch Paper',
                        filter: false,
                        minWidth: 200,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'isCovenantCrossPermissioned',
                        headerName: 'Is Covenant Cross-Permissioned',
                        filter: false,
                        minWidth: 200,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'hasBixbyCompliance',
                        headerName: 'Has Compliance',
                        filter: false,
                        minWidth: 200,

                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'hasBixbyComplianceAutoApproval',
                        headerName: 'Has Compliance Auto Approval',
                        filter: false,
                        minWidth: 250,
                        cellRenderer: CheckboxCellRenderer,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'hasBixbyComplianceDemoMode',
                        headerName: 'Has Compliance Demo Mode',
                        filter: false,
                        minWidth: 250,
                        cellRenderer: CheckboxCellRenderer,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'allowCreditSightsClientRoles',
                        headerName: 'Allow CreditSights Client Roles',
                        filter: false,
                        minWidth: 200,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'usersCannotLogin',
                        headerName: 'Users Cannot Login',
                        filter: false,
                        minWidth: 200,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'hasSFDR',
                        headerName: 'Has SFDR',
                        filter: false,
                        minWidth: 200,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'hasUsEuroGrouping',
                        headerName: 'Has US/Euro Grouping',
                        filter: false,
                        minWidth: 200,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                      {
                        field: 'isInPreview',
                        headerName: 'Is In Preview',
                        filter: false,
                        minWidth: 200,
                        valueFormatter: (params: ValueFormatterParams) => {
                          return params.value ? 'Yes' : '';
                        },
                      },
                    ]}></AgGridReact>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <div className="clients_no_data">
            <NoDataFound dataType="clients" />
          </div>
        )}
        <Modal title="Add New Client Profile" active={modalActive} forForm onClose={handleModalDismiss}>
          <AddClient isActive={modalActive} onSubmit={handleAdd} />
        </Modal>
      </React.Fragment>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.clientState.isLoading,
    clients: state.clientState.clients || [],
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetClients: () => dispatch(getAllClients()),
  dispatchSetCurrentClient: (clientSlim?: Bixby.Models.ClientSlim) => dispatch(setCurrentClient(clientSlim)),
  dispatchAddClient: (client: Bixby.Models.CreateClientRequest) => dispatch(createClient(client)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientList);
